import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { styled } from "@mui/material/styles";

import BookmarkMarkers from "./BookmarkMarkers";
import AddBookmarkDialog from "./AddBookmarkDialog";
import BookmarkListDialog from "./BookmarkListDialog";
import BookTableOfContentsDialog from "arena/components/Book/BookTableOfContentsDialog";
import { VIEWER_BOOKMARKS } from "arena/queries";
import { shareURL } from "@telegram-apps/sdk";
import { isTelegramMiniApp } from "core/utils/telegram";
import { useArena } from "core/components/ArenaProvider";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Slider from "@mui/material/Slider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ListIcon from "@mui/icons-material/List";
import CloseIcon from "@mui/icons-material/Close";
import IosShareIcon from "@mui/icons-material/IosShare";

// Стилизованная кнопка для закладок (как в Shorty плеере)
const BookmarkButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "darkMode",
})(({ theme, darkMode }) => ({
  background: darkMode ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)",
  color: darkMode ? "white" : "black",
  backdropFilter: "blur(4px)",
  width: 56,
  height: 56,
  "&:hover": {
    background: darkMode ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.8)",
    transform: "scale(1.1)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  transition: "all 0.2s ease-in-out",

  // Уменьшаем размер на мобильных
  [theme.breakpoints.down("md")]: {
    width: 48,
    height: 48,
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
}));

// Общие стили для LinearProgress
const getLinearProgressStyles = (darkMode, theme) => ({
  height: 4,
  backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
  "& .MuiLinearProgress-bar": {
    backgroundColor: theme.palette.primary.main,
  },
});

/**
 * Компонент элементов управления читалкой
 * Включает AppBar, настройки, навигацию и прогресс
 */
export default function ReaderControls({
  bookTitle,
  contentId,
  bookId,
  readerControls,
  uiVisible,
  onNavigateToPosition,
  content,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("arena");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const arena = useArena();

  // Локальные состояния UI
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addBookmarkOpen, setAddBookmarkOpen] = useState(false);
  const [bookmarkListOpen, setBookmarkListOpen] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  // Загружаем закладки пользователя
  const { data: bookmarksData, refetch: refetchBookmarks } = useQuery(
    VIEWER_BOOKMARKS,
    {
      variables: { contentId },
      skip: !contentId,
    }
  );

  const bookmarks = bookmarksData?.viewerBookmarks || [];

  const handleBackClick = () => {
    navigate(`/book/${contentId}`);
  };

  const handleSettingsToggle = () => {
    setSettingsOpen((prev) => !prev);
  };

  const handleTocToggle = () => {
    setTocOpen((prev) => !prev);
  };

  const handleAddBookmarkClick = () => {
    setAddBookmarkOpen(true);
  };

  const handleBookmarkListClick = () => {
    setBookmarkListOpen(true);
  };

  const handleBookmarkClick = (bookmark) => {
    onNavigateToPosition?.(bookmark.character);
  };

  const handleBookmarkCreated = () => {
    refetchBookmarks();
  };

  const handleBookmarkDeleted = () => {
    refetchBookmarks();
  };

  const handleChapterClick = (chapterIndex) => {
    readerControls.goToChapter?.(chapterIndex);
    setTocOpen(false); // Закрываем диалог после навигации
  };

  // Проверка доступности функции поделиться
  const isShareAvailable = () => {
    if (!process.browser) return false;
    if (!arena?.isShareEnabled) return false; // Проверка настроек арены

    if (process.env.NODE_ENV === "production") {
      if (isTelegramMiniApp) {
        return shareURL.isAvailable();
      } else {
        return "share" in navigator;
      }
    }
    return true; // В dev режиме всегда показываем
  };

  const handleShare = () => {
    let shareUrl = content?.shareUrl || window.location.href;

    // Добавляем characterPosition в URL вместо CFI
    if (readerControls.currentCharacter !== undefined) {
      const url = new URL(shareUrl, window.location.origin);
      url.searchParams.set("pos", readerControls.currentCharacter);
      shareUrl = url.toString();
    }

    const shareData = {
      title: bookTitle,
      text: `Читаю "${bookTitle}"`,
      url: shareUrl,
    };

    if (isTelegramMiniApp) {
      shareURL(shareData.url, shareData.title);
    } else if ("share" in navigator) {
      navigator.share(shareData).catch(console.error);
    }
  };

  return (
    <>
      {/* AppBar с заголовком и кнопками */}
      <Fade in={uiVisible}>
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            background: readerControls.darkMode
              ? "rgba(18, 18, 18, 0.7)"
              : "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            color: readerControls.darkMode ? "#ffffff" : "#000000",
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBackClick}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {bookTitle}
            </Typography>

            <IconButton color="inherit" onClick={handleSettingsToggle}>
              <SettingsIcon />
            </IconButton>
          </Toolbar>

          {/* Прогресс-бар с маркерами закладок */}
          <Box sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={readerControls.readingProgress}
              sx={getLinearProgressStyles(readerControls.darkMode, theme)}
            />
            <BookmarkMarkers
              bookmarks={bookmarks}
              totalCharacters={readerControls.totalCharacters}
              onBookmarkClick={handleBookmarkClick}
              placement="top"
            />
          </Box>
        </AppBar>
      </Fade>

      {/* Плавающие кнопки закладок справа под AppBar */}
      <Fade in={uiVisible} timeout={300}>
        <Box
          sx={{
            position: "fixed",
            top: { xs: 72, sm: 80 }, // Адаптивно под AppBar
            right: { xs: 12, sm: 16 },
            zIndex: theme.zIndex.drawer + 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <BookmarkButton
            darkMode={readerControls.darkMode}
            onClick={handleTocToggle}
          >
            <ListIcon />
          </BookmarkButton>

          <BookmarkButton
            darkMode={readerControls.darkMode}
            onClick={handleAddBookmarkClick}
          >
            <BookmarkAddIcon />
          </BookmarkButton>

          <BookmarkButton
            darkMode={readerControls.darkMode}
            onClick={handleBookmarkListClick}
          >
            <BookmarkIcon />
          </BookmarkButton>

          {isShareAvailable() && (
            <BookmarkButton
              darkMode={readerControls.darkMode}
              onClick={handleShare}
            >
              <IosShareIcon />
            </BookmarkButton>
          )}
        </Box>
      </Fade>

      {/* Прогресс-бар внизу экрана (когда UI скрыт) */}
      <Fade in={!uiVisible}>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Box sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={readerControls.readingProgress}
              sx={getLinearProgressStyles(readerControls.darkMode, theme)}
            />
            <BookmarkMarkers
              bookmarks={bookmarks}
              totalCharacters={readerControls.totalCharacters}
              onBookmarkClick={handleBookmarkClick}
            />
          </Box>
        </Box>
      </Fade>

      {/* Drawer с настройками */}
      <SwipeableDrawer
        anchor="right"
        open={settingsOpen}
        onClose={handleSettingsToggle}
        onOpen={handleSettingsToggle}
        sx={{
          zIndex: theme.zIndex.drawer + 10,
          "& .MuiDrawer-paper": {
            width: isMobile ? "100%" : 320,
            maxWidth: "100vw",
            zIndex: theme.zIndex.drawer + 10,
          },
        }}
      >
        {/* Заголовок с кнопкой закрыть */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">
            {t("reader.settings.title", "Настройки чтения")}
          </Typography>
          <IconButton onClick={handleSettingsToggle}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ p: 2 }}>
          {/* Размер шрифта */}
          <ListItem>
            <Box sx={{ width: "100%" }}>
              <Typography gutterBottom>
                {t("reader.settings.fontSize", "Размер шрифта")}:{" "}
                {readerControls.fontSize}px
              </Typography>
              <Slider
                value={readerControls.fontSize}
                onChange={(_, value) => readerControls.setFontSize(value)}
                min={12}
                max={24}
                step={1}
                marks={[
                  { value: 12, label: "12px" },
                  { value: 16, label: "16px" },
                  { value: 20, label: "20px" },
                  { value: 24, label: "24px" },
                ]}
              />
            </Box>
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {/* Темная тема */}
          <ListItem>
            <FormControlLabel
              control={
                <Switch
                  checked={readerControls.darkMode}
                  onChange={(e) => readerControls.setDarkMode(e.target.checked)}
                />
              }
              label={t("reader.settings.darkMode", "Темная тема")}
            />
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {/* Прогресс чтения */}
          <ListItem>
            <ListItemText
              primary={t("reader.progress", "Прогресс чтения")}
              secondary={`${readerControls.readingProgress.toFixed(1)}%`}
            />
          </ListItem>
        </List>
      </SwipeableDrawer>

      {/* Диалоги управления закладками */}
      <AddBookmarkDialog
        open={addBookmarkOpen}
        onClose={() => setAddBookmarkOpen(false)}
        contentId={contentId}
        bookId={bookId}
        currentCharacter={readerControls.currentCharacter}
        currentChapterName={readerControls.currentChapter?.title || ""}
        progressPercent={readerControls.readingProgress}
        onBookmarkCreated={handleBookmarkCreated}
      />

      <BookmarkListDialog
        open={bookmarkListOpen}
        onClose={() => setBookmarkListOpen(false)}
        bookmarks={bookmarks}
        onBookmarkClick={handleBookmarkClick}
        onBookmarkDeleted={handleBookmarkDeleted}
      />

      <BookTableOfContentsDialog
        open={tocOpen}
        onClose={() => setTocOpen(false)}
        tableOfContents={readerControls.tableOfContents || []}
        bookTitle={bookTitle}
        contentId={contentId}
        hasAccess={true} // В читалке доступ уже есть
        onChapterClick={handleChapterClick}
        currentChapter={readerControls.currentChapter}
      />
    </>
  );
}

ReaderControls.propTypes = {
  bookTitle: PropTypes.string.isRequired,
  contentId: PropTypes.string.isRequired,
  bookId: PropTypes.string.isRequired,
  uiVisible: PropTypes.bool.isRequired,
  onNavigateToPosition: PropTypes.func,
  content: PropTypes.object,
  readerControls: PropTypes.shape({
    fontSize: PropTypes.number.isRequired,
    setFontSize: PropTypes.func.isRequired,
    darkMode: PropTypes.bool.isRequired,
    setDarkMode: PropTypes.func.isRequired,
    readingProgress: PropTypes.number.isRequired,
    currentCharacter: PropTypes.number.isRequired,
    totalCharacters: PropTypes.number.isRequired,
    currentCfi: PropTypes.string,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    tableOfContents: PropTypes.arrayOf(PropTypes.object),
    currentChapter: PropTypes.object,
    goToChapter: PropTypes.func,
  }).isRequired,
};
